import React from 'react';
import { Button } from 'primereact/button';
import {
    SP_SKIP, extractResponseData, SmartStyles, SmartHeader, SmartSection, NoRecordsCard
} from './_SmartFieldRenderer';

// `tan` alongside `tan_number`: the plain /tan/ endpoint returns the number as
// `tan` while the richer tan-advanced payload calls it `tan_number`. Without
// both, the TAN itself dropped through to OTHER DETAILS — the one field nobody
// expects to hunt for. Kept in step with TanVerifyPdfController::SECTIONS.
const GROUPS = {
    tan:      { title: 'TAN DETAILS', icon: 'pi-id-card',    keys: ['tan_number', 'tan', 'name', 'status', 'category'] },
    entity:   { title: 'ENTITY',      icon: 'pi-briefcase',  keys: ['pan_linked_status'] },
    location: { title: 'LOCATION',    icon: 'pi-map-marker', keys: ['city', 'state', 'pin'] },
};

const HIGHLIGHT = ['tan_number', 'tan'];

// `onDownloadPDF`/`pdfPasswordHint` are optional. The shared-link and lead
// history views render this component without them, and those readers have no
// business regenerating a billed report's PDF — so the button only appears when
// the host actually wires it up.
const TanVerifyOutputComponent = ({ reportData, hideCredits, isSharedView, onDownloadPDF, pdfPasswordHint }) => {
    const shouldHideCredits = hideCredits || isSharedView;
    const { responseData, remainingBalance, errorMsg, hasRealData } = extractResponseData(reportData);

    if (!reportData) return <NoRecordsCard subtitle="No data available" />;
    if (!responseData || !hasRealData) {
        return <NoRecordsCard errorMsg={errorMsg} subtitle="No records found for the provided details. Please verify and try again." />;
    }

    const d = responseData;
    const grouped = {};
    for (const [groupName, group] of Object.entries(GROUPS)) {
        const picked = {};
        group.keys.forEach(k => { if (k in d && d[k] !== null && d[k] !== undefined && d[k] !== '') picked[k] = d[k]; });
        if (Object.keys(picked).length > 0) grouped[groupName] = picked;
    }
    const knownKeys = new Set(Object.values(GROUPS).flatMap(g => g.keys));
    const leftover = {};
    for (const [k, v] of Object.entries(d)) {
        if (SP_SKIP.has(k) || knownKeys.has(k)) continue;
        if (v === null || v === undefined || v === '') continue;
        leftover[k] = v;
    }

    return (
        <div>
            <SmartStyles />
            <SmartHeader title="TAN Verification Report" icon="pi-id-card" remainingBalance={remainingBalance} shouldHideCredits={shouldHideCredits} />
            <div className="horizontal-report-card">
                {onDownloadPDF && (
                    <>
                        <div className="flex justify-content-end flex-wrap gap-2 mb-2">
                            <Button
                                label="Download PDF"
                                icon="pi pi-file-pdf"
                                size="small"
                                outlined
                                onClick={onDownloadPDF}
                            />
                        </div>
                        {pdfPasswordHint && (
                            <div className="surface-100 border-round border-left-3 border-primary p-2 mb-2 flex align-items-start gap-2">
                                <i className="pi pi-lock text-primary text-sm mt-1" aria-hidden="true" />
                                <span className="text-600 text-sm line-height-3">{pdfPasswordHint}</span>
                            </div>
                        )}
                    </>
                )}
                {Object.entries(GROUPS).map(([groupName, meta]) => (
                    grouped[groupName]
                        ? <SmartSection key={groupName} title={meta.title} icon={meta.icon} data={grouped[groupName]} highlightKeys={HIGHLIGHT} />
                        : null
                ))}
                {Object.keys(leftover).length > 0 && (
                    <SmartSection title="OTHER DETAILS" icon="pi-info-circle" data={leftover} />
                )}
            </div>
        </div>
    );
};

export default TanVerifyOutputComponent;
